/**
 * Global behaviours: accordions, scroll reveal, cart operations, quantity input.
 */

/* ---------------------------------------------------------------------------
   <lj-accordion> — one or more collapsible rows
   Uses grid-template-rows 0fr/1fr so height animates without measuring JS.
   --------------------------------------------------------------------------- */
class LJAccordion extends HTMLElement {
  connectedCallback() {
    this.single = this.hasAttribute('single');
    this.triggers = Array.from(this.querySelectorAll('[data-accordion-trigger]'));

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => this.toggle(trigger));
    });
  }

  toggle(trigger) {
    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;

    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    // In single mode, opening one row closes the others
    if (this.single && !isOpen) {
      this.triggers.forEach((other) => {
        if (other === trigger) return;
        const otherPanel = document.getElementById(other.getAttribute('aria-controls'));
        other.setAttribute('aria-expanded', 'false');
        if (otherPanel) otherPanel.dataset.open = 'false';
      });
    }

    trigger.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
    panel.dataset.open = isOpen ? 'false' : 'true';
  }
}
customElements.define('lj-accordion', LJAccordion);

/* ---------------------------------------------------------------------------
   <lj-quantity> — quantity stepper
   --------------------------------------------------------------------------- */
class LJQuantity extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;

    this.querySelectorAll('[data-qty-change]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const delta = parseInt(btn.dataset.qtyChange, 10);
        const min = parseInt(this.input.min, 10) || 0;
        const next = Math.max(min, (parseInt(this.input.value, 10) || 0) + delta);
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }
}
customElements.define('lj-quantity', LJQuantity);

/* ---------------------------------------------------------------------------
   <lj-tabs> — tab list with roving focus, following the ARIA tabs pattern
   --------------------------------------------------------------------------- */
class LJTabs extends HTMLElement {
  connectedCallback() {
    this.triggers = Array.from(this.querySelectorAll('[data-tab-trigger]'));
    this.panels = Array.from(this.querySelectorAll('[data-tab-panel]'));

    this.triggers.forEach((trigger, index) => {
      // Only the active tab is tabbable; arrow keys move between the rest.
      trigger.tabIndex = trigger.getAttribute('aria-selected') === 'true' ? 0 : -1;
      trigger.addEventListener('click', () => this.select(index));
      trigger.addEventListener('keydown', (event) => this.onKeydown(event, index));
    });
  }

  select(index) {
    this.triggers.forEach((trigger, i) => {
      const active = i === index;
      trigger.setAttribute('aria-selected', active ? 'true' : 'false');
      trigger.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    this.revealTrigger(this.triggers[index]);
  }

  /* Where the tab strip scrolls sideways (the ingredients section builds one
     tab per ingredient group, so it can outrun the viewport), the tab just
     chosen may sit half off-screen. Nudge the STRIP only -- scrollIntoView
     would walk up and scroll the page as well. */
  revealTrigger(trigger) {
    if (!trigger) return;
    const strip = trigger.closest('[role="tablist"]') || trigger.parentElement;
    if (!strip || strip.scrollWidth <= strip.clientWidth) return;

    const t = trigger.getBoundingClientRect();
    const s = strip.getBoundingClientRect();
    const margin = 16;
    let delta = 0;
    if (t.left < s.left + margin) delta = t.left - s.left - margin;
    else if (t.right > s.right - margin) delta = t.right - s.right + margin;
    if (!delta) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    strip.scrollBy({ left: delta, behavior: reduced ? 'auto' : 'smooth' });
  }

  onKeydown(event, index) {
    const last = this.triggers.length - 1;
    let next = null;

    if (event.key === 'ArrowRight') next = index === last ? 0 : index + 1;
    else if (event.key === 'ArrowLeft') next = index === 0 ? last : index - 1;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = last;
    else return;

    event.preventDefault();
    this.select(next);
    this.triggers[next].focus();
  }
}
customElements.define('lj-tabs', LJTabs);

/* ---------------------------------------------------------------------------
   Scroll reveal — fades sections in as they enter the viewport
   --------------------------------------------------------------------------- */
(function initReveal() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const targets = document.querySelectorAll('.reveal');

  if (reduced || !('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-visible');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -60px 0px', threshold: 0.05 });

  targets.forEach((el) => observer.observe(el));
})();

/* ---------------------------------------------------------------------------
   Cart — add/update via the AJAX API, then refresh the drawer
   --------------------------------------------------------------------------- */
const LJCart = {
  routes: window.LJRoutes || {},

  async add(items) {
    const res = await fetch(`${this.routes.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({}));
      throw new Error(error.description || 'Could not add to cart');
    }

    const data = await res.json();
    document.dispatchEvent(new CustomEvent('lj:cart:updated'));
    return data;
  },

  async change(line, quantity) {
    const res = await fetch(`${this.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line, quantity })
    });

    const data = await res.json();
    document.dispatchEvent(new CustomEvent('lj:cart:updated'));
    return data;
  },

  /* Change a line by VARIANT id rather than line index. A product card knows
     which variant it sells but not where that line sits in the cart, and the
     index shifts as other lines are removed. */
  async changeById(id, quantity) {
    const res = await fetch(`${this.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: String(id), quantity })
    });

    const data = await res.json();
    document.dispatchEvent(new CustomEvent('lj:cart:updated'));
    return data;
  },

  async get() {
    const res = await fetch(`${this.routes.cart_url}.js`);
    return res.json();
  }
};

window.LJCart = LJCart;

/* ---------------------------------------------------------------------------
   Toast — the pill the reference drops in after a cart change

   Timing measured on the reference: it enters over 0.35s, sits for 4s, then
   leaves on the same curve. A second cart change replaces the message in place
   rather than stacking a second pill.
   --------------------------------------------------------------------------- */
const LJToast = {
  /* 3650 + the 350ms exit = 4000ms on screen, which is what the reference
     measured (appeared 708ms, gone 4732ms after the click). Timing the whole
     thing at 4000 made ours sit ~500ms longer than the original. */
  DURATION: 3650,
  EXIT: 350,

  show(message) {
    const host = document.querySelector('[data-toast-host]');
    const template = document.querySelector('[data-toast-template]');
    if (!host || !template || !message) return;

    clearTimeout(this.hideTimer);
    clearTimeout(this.removeTimer);

    let toast = host.firstElementChild;
    if (!toast) {
      toast = template.content.firstElementChild.cloneNode(true);
      host.appendChild(toast);
    } else {
      // Re-entering: drop the leaving state so the pill settles back.
      toast.removeAttribute('data-leaving');
    }

    toast.querySelector('[data-toast-message]').textContent = message;

    this.hideTimer = setTimeout(() => {
      toast.setAttribute('data-leaving', '');
      this.removeTimer = setTimeout(() => toast.remove(), this.EXIT);
    }, this.DURATION);
  }
};

window.LJToast = LJToast;

/* ---------------------------------------------------------------------------
   Product cards — swap ADD for a quantity stepper once the line is in the cart

   Liquid renders the correct state on first paint; this keeps every card on the
   page in step afterwards, including cards for the same variant in two grids.
   --------------------------------------------------------------------------- */
async function syncProductCards() {
  const ctas = document.querySelectorAll('[data-card-cta]');
  if (!ctas.length) return;

  let cart;
  try {
    cart = await LJCart.get();
  } catch (error) {
    return; // leave the cards as they are rather than blanking them
  }

  const byVariant = new Map();
  (cart.items || []).forEach((item) => {
    const id = item.variant_id || item.id;
    byVariant.set(id, (byVariant.get(id) || 0) + item.quantity);
  });

  ctas.forEach((form) => {
    const quantity = byVariant.get(Number(form.dataset.variantId)) || 0;
    const stepper = form.querySelector('[data-card-stepper]');
    const add = form.querySelector('[data-card-add]');
    const count = form.querySelector('[data-step-count]');

    if (count) count.textContent = quantity;
    if (stepper) stepper.hidden = quantity === 0;
    if (add) add.hidden = quantity > 0;
  });
}

document.addEventListener('lj:cart:updated', syncProductCards);

/* One listener for every cart path -- the card ADD, the stepper, the drawer --
   so the toast never has to be fired by hand at each call site. */
document.addEventListener('lj:cart:updated', () => {
  LJToast.show((window.LJStrings && window.LJStrings.cartUpdated) || 'Cart updated successfully');
});

document.addEventListener('click', async (event) => {
  const step = event.target.closest('[data-step]');
  if (!step) return;

  event.preventDefault();
  const form = step.closest('[data-card-cta]');
  if (!form || step.disabled) return;

  const count = form.querySelector('[data-step-count]');
  const current = parseInt(count?.textContent, 10) || 0;
  const next = Math.max(0, current + Number(step.dataset.step));
  const buttons = form.querySelectorAll('[data-step]');

  buttons.forEach((b) => { b.disabled = true; });
  try {
    await LJCart.changeById(Number(form.dataset.variantId), next);
  } catch (error) {
    // The sync below puts the count back to whatever the cart really holds.
    document.dispatchEvent(new CustomEvent('lj:cart:updated'));
  } finally {
    buttons.forEach((b) => { b.disabled = false; });
  }
});

/* ---------------------------------------------------------------------------
   Product forms — intercept submit so adding never leaves the page
   --------------------------------------------------------------------------- */
document.addEventListener('submit', async (event) => {
  const form = event.target.closest('[data-product-form]');
  if (!form) return;

  // "Buy now" submits with name="checkout" and must reach Shopify so the
  // shopper lands on checkout — only the add-to-cart path is intercepted.
  if (event.submitter && event.submitter.name === 'checkout') return;

  event.preventDefault();

  // Use the button that actually submitted, so a form with several submit
  // buttons shows the pending state on the one the shopper pressed.
  const button = event.submitter || form.querySelector('[data-atc]') || form.querySelector('[type="submit"]');
  const idInput = form.querySelector('[name="id"]');
  if (!idInput || !idInput.value) return;

  /* A spinner, not a word. Writing "Adding…" into the button overflowed the
     48x28 ADD chip on a collection card, and swapping textContent also
     flattened the label/note spans inside it. The class leaves the content
     alone and lets CSS cover it. */
  if (button) {
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
  }

  try {
    const qtyInput = form.querySelector('[name="quantity"]');
    await LJCart.add([
      {
        id: Number(idInput.value),
        quantity: parseInt(qtyInput?.value, 10) || 1
      }
    ]);

    document.dispatchEvent(new CustomEvent('lj:cart:open'));
  } catch (error) {
    const errorEl = form.querySelector('[data-form-error]');
    if (errorEl) {
      errorEl.textContent = error.message;
      errorEl.hidden = false;
    }
  } finally {
    if (button) {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
      button.classList.remove('is-loading');
    }
  }
});
